import React, { useState } from 'react';
import { Check, KeyRound } from 'lucide-react';
import * as profileApi from '../../api/profile';

export const MIN_PASSWORD_LENGTH = 8;

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };

/**
 * Change your own password, from the profile page.
 *
 * Collapsed until asked for - it is a rare action sitting among fields people
 * come here to read, and three password boxes on permanent display make the page
 * look like a security form rather than a profile.
 *
 * The confirm box is checked here rather than at the server: mistyping the new
 * password twice is a slip, not a rejected request, and it never needs a round
 * trip to tell you so. Everything the server owns - whether the current password
 * is right, whether the new one is actually new - comes back from it.
 */
function ChangePasswordField() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const setField = (name) => (e) => setForm({ ...form, [name]: e.target.value });

  const close = () => {
    setForm(EMPTY_FORM);
    setError('');
    setOpen(false);
  };

  const save = async () => {
    if (form.newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setError('');
    setBusy(true);
    try {
      await profileApi.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      // The typed passwords go the moment they are no longer needed - this form
      // sits on a page people leave open.
      setForm(EMPTY_FORM);
      setOpen(false);
      setSaved(true);
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Could not change your password');
      } else {
        setError('Could not change your password');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-field change-password-field">
      <span className="field-label">Password:</span>

      {open ? (
        <div className="change-password-form">
          <label className="change-password-row">
            <span>Current password</span>
            <input
              type="password"
              value={form.currentPassword}
              autoComplete="current-password"
              onChange={setField('currentPassword')}
              disabled={busy}
            />
          </label>

          <label className="change-password-row">
            <span>New password</span>
            <input
              type="password"
              value={form.newPassword}
              autoComplete="new-password"
              onChange={setField('newPassword')}
              disabled={busy}
            />
          </label>

          <label className="change-password-row">
            <span>Confirm new password</span>
            <input
              type="password"
              value={form.confirmPassword}
              autoComplete="new-password"
              onChange={setField('confirmPassword')}
              disabled={busy}
            />
          </label>

          {error && <span className="error-text">{error}</span>}

          <div className="change-password-actions">
            <button className="primary-btn" onClick={save} disabled={busy}>
              <Check size={16} />
              {busy ? 'Saving…' : 'Save password'}
            </button>
            <button className="secondary-btn" onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>

          <span className="helper-text">
            At least {MIN_PASSWORD_LENGTH} characters, and different from your
            current one.
          </span>
        </div>
      ) : (
        <span className="change-password-view">
          <span className="field-value">••••••••</span>
          <button
            className="secondary-btn"
            onClick={() => {
              setSaved(false);
              setOpen(true);
            }}
          >
            <KeyRound size={14} />
            Change password
          </button>
          {saved && <span className="success-text">Password changed</span>}
        </span>
      )}
    </div>
  );
}

export default ChangePasswordField;
